"use client";

import {
  AlertTriangle,
  CheckCircle2,
  CircleX,
  Loader2,
  X,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FocusEvent,
} from "react";
import { cn } from "@/lib/utils";
import type {
  OperationalAlert as OperationalAlertModel,
  OperationalAlertSeverity,
} from "@/types/operational-alert";

const TRANSIENT_ALERT_DURATION_MS = 6_000;
const ALERT_EXIT_ANIMATION_DURATION_MS = 500;

const severityPresentation: Record<
  OperationalAlertSeverity,
  {
    icon: typeof CircleX;
    label: string;
    classes: string;
    iconClasses: string;
  }
> = {
  error: {
    icon: CircleX,
    label: "Erro",
    classes:
      "border-[color:var(--color-danger)] bg-[var(--color-danger-light)]",
    iconClasses: "text-[var(--color-danger-foreground)]",
  },
  warning: {
    icon: AlertTriangle,
    label: "Atenção",
    classes:
      "border-[color:var(--color-warning)] bg-[var(--color-warning-light)]",
    iconClasses: "text-[var(--color-warning-foreground)]",
  },
  "partial-success": {
    icon: CheckCircle2,
    label: "Concluído parcialmente",
    classes: "border-[color:var(--color-info)] bg-[var(--color-info-light)]",
    iconClasses: "text-[var(--color-info-foreground)]",
  },
  success: {
    icon: CheckCircle2,
    label: "Sucesso",
    classes:
      "border-[color:var(--color-success)] bg-[var(--color-success-light)]",
    iconClasses: "text-[var(--color-success)]",
  },
};

function renderDescription(description: string, descriptionHighlight?: string) {
  if (!descriptionHighlight) {
    return description;
  }

  const highlightStart = description.indexOf(descriptionHighlight);
  if (highlightStart === -1) {
    return description;
  }

  const highlightEnd = highlightStart + descriptionHighlight.length;

  return (
    <>
      {description.slice(0, highlightStart)}
      <strong className="font-bold">{descriptionHighlight}</strong>
      {description.slice(highlightEnd)}
    </>
  );
}

type OperationalAlertProps = {
  alert: OperationalAlertModel;
  onDismiss: (id: string) => void;
};

export function OperationalAlert({ alert, onDismiss }: OperationalAlertProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [hasFocusWithin, setHasFocusWithin] = useState(false);
  const [isActionPending, setIsActionPending] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const remainingTime = useRef(TRANSIENT_ALERT_DURATION_MS);
  const timerStartedAt = useRef(0);
  const timerRevision = useRef(alert.revision);
  const dismissTimeout = useRef<number | null>(null);
  const isTransient = alert.severity !== "error" && !alert.persistent;
  const isPaused = isHovered || hasFocusWithin || isActionPending || isExiting;
  const isRtl =
    typeof document !== "undefined" &&
    (document.documentElement.dir || document.body.dir).toLowerCase() === "rtl";
  const presentation = severityPresentation[alert.severity];
  const SeverityIcon = presentation.icon;

  useEffect(() => {
    return () => {
      if (dismissTimeout.current !== null) {
        window.clearTimeout(dismissTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    if (timerRevision.current === alert.revision) {
      return;
    }

    if (dismissTimeout.current !== null) {
      window.clearTimeout(dismissTimeout.current);
      dismissTimeout.current = null;
    }
    setIsExiting(false);
  }, [alert.revision]);

  const requestDismiss = useCallback(() => {
    if (isExiting) {
      return;
    }

    setIsExiting(true);
    const prefersReducedMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (prefersReducedMotion) {
      onDismiss(alert.id);
      return;
    }

    dismissTimeout.current = window.setTimeout(() => {
      dismissTimeout.current = null;
      onDismiss(alert.id);
    }, ALERT_EXIT_ANIMATION_DURATION_MS);
  }, [alert.id, isExiting, onDismiss]);

  useEffect(() => {
    if (!isTransient || isPaused) {
      return;
    }

    if (timerRevision.current !== alert.revision) {
      remainingTime.current = TRANSIENT_ALERT_DURATION_MS;
      timerRevision.current = alert.revision;
    }

    let didComplete = false;
    timerStartedAt.current = Date.now();
    const timeoutId = window.setTimeout(() => {
      didComplete = true;
      requestDismiss();
    }, remainingTime.current);

    return () => {
      window.clearTimeout(timeoutId);
      if (!didComplete) {
        const elapsed = Date.now() - timerStartedAt.current;
        remainingTime.current = Math.max(0, remainingTime.current - elapsed);
      }
    };
  }, [alert.id, alert.revision, isPaused, isTransient, requestDismiss]);

  const handleBlur = useCallback((event: FocusEvent<HTMLElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setHasFocusWithin(false);
    }
  }, []);

  const handleAction = useCallback(() => {
    if (!alert.action || isActionPending) {
      return;
    }

    setIsActionPending(true);
    Promise.resolve(alert.action.onAction())
      .catch(() => undefined)
      .finally(() => setIsActionPending(false));
  }, [alert.action, isActionPending]);

  return (
    <article
      role={alert.severity === "error" ? "alert" : "status"}
      aria-live={alert.severity === "error" ? "assertive" : "polite"}
      aria-atomic="true"
      aria-label={`${presentation.label}: ${alert.title}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setHasFocusWithin(true)}
      onBlur={handleBlur}
      className={cn(
        "pointer-events-auto w-full overflow-hidden rounded-xl border-l-4 shadow-lg shadow-slate-900/10",
        "animate__animated animate__faster",
        isExiting
          ? isRtl
            ? "animate__slideOutLeft"
            : "animate__slideOutRight"
          : isRtl
            ? "animate__slideInLeft"
            : "animate__slideInRight",
        presentation.classes,
      )}
    >
      <div className="flex min-w-0 items-start gap-3 p-4">
        <SeverityIcon
          aria-hidden="true"
          className={cn("mt-0.5 h-5 w-5 shrink-0", presentation.iconClasses)}
        />

        <div className="min-w-0 flex-1">
          <p className={cn("text-xs font-semibold uppercase tracking-wide", presentation.iconClasses)}>
            {presentation.label}
          </p>
          <h2 className="mt-0.5 break-words text-sm font-semibold text-[var(--color-text-primary)]">
            {alert.title}
          </h2>
          <p className="mt-1 break-words text-sm leading-relaxed text-[var(--color-text-primary)]">
            {renderDescription(alert.description, alert.descriptionHighlight)}
          </p>

          {alert.action ? (
            <button
              type="button"
              disabled={isActionPending}
              onClick={handleAction}
              className={cn(
                "mt-3 inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm font-semibold text-[var(--color-brand-primary)]",
                "outline-none transition hover:bg-[var(--color-surface-soft)] focus-visible:ring-2 focus-visible:ring-[var(--color-brand-primary)] focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60",
              )}
            >
              {isActionPending ? (
                <Loader2
                  aria-hidden="true"
                  className="h-4 w-4 animate-spin motion-reduce:animate-none"
                />
              ) : null}
              {alert.action.label}
            </button>
          ) : null}
        </div>

        {alert.dismissible !== false ? (
          <button
            type="button"
            aria-label={`Dispensar alerta: ${alert.title}`}
            onClick={requestDismiss}
            className={cn(
              "-mr-1 -mt-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-[var(--color-text-primary)]",
              "outline-none transition hover:bg-black/5 hover:text-[var(--color-text-primary)] focus-visible:ring-2 focus-visible:ring-[var(--color-brand-primary)] focus-visible:ring-offset-2",
            )}
          >
            <X aria-hidden="true" className="h-5 w-5" />
          </button>
        ) : null}
      </div>
    </article>
  );
}
